import React, { useState } from 'react';
import { BookOpen, Plus, Edit2, Trash2, Save, X, AlertCircle } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

const SubjectsSection = ({ 
  formData, 
  handleInputChange, 
  handleArrayChange,
  errors 
}) => {
  // Состояние формы для добавления/редактирования предмета
  const [subjectForm, setSubjectForm] = useState({
    id: null,
    name: '',
    hoursPerWeek: 1,
    color: '#4F46E5'
  });
  
  // Флаг, показывающий, находимся ли мы в режиме редактирования
  const [isEditing, setIsEditing] = useState(false);
  
  // Доступные цвета для предметов
  const availableColors = [
    { value: '#4F46E5', name: 'Индиго' },
    { value: '#16A34A', name: 'Зеленый' },
    { value: '#CA8A04', name: 'Желтый' },
    { value: '#DC2626', name: 'Красный' },
    { value: '#0EA5E9', name: 'Голубой' },
    { value: '#8B5CF6', name: 'Фиолетовый' },
    { value: '#EC4899', name: 'Розовый' },
    { value: '#F97316', name: 'Оранжевый' }
  ];
  
  // Функция сброса формы
  const resetForm = () => {
    setSubjectForm({
      id: null,
      name: '',
      hoursPerWeek: 1,
      color: '#4F46E5'
    });
    setIsEditing(false);
  };
  
  // Функция для обработки изменений в форме предмета
  const handleSubjectFormChange = (e) => {
    const { name, value } = e.target;
    setSubjectForm(prev => ({
      ...prev,
      [name]: name === 'hoursPerWeek' ? parseInt(value) || 1 : value
    }));
  };
  
  // Функция для добавления/обновления предмета
  const handleSubmitSubject = (e) => {
    e.preventDefault();
    
    // Проверка заполнения названия
    if (!subjectForm.name.trim()) {
      alert('Пожалуйста, введите название предмета');
      return;
    }
    
    // Проверка часов в неделю
    if (subjectForm.hoursPerWeek < 1 || subjectForm.hoursPerWeek > 20) {
      alert('Недельная нагрузка должна быть от 1 до 20 часов');
      return;
    }
    
    if (isEditing) {
      // Обновление существующего предмета
      handleArrayChange('subjects', 'update', subjectForm);
    } else {
      // Добавление нового предмета
      handleArrayChange('subjects', 'add', subjectForm);
    }
    
    // Сброс формы
    resetForm();
  };
  
  // Функция для начала редактирования предмета
  const startEditingSubject = (subject) => {
    setSubjectForm({
      id: subject.id,
      name: subject.name,
      hoursPerWeek: subject.hoursPerWeek,
      color: subject.color
    });
    setIsEditing(true);
  };
  
  // Функция для удаления предмета
  const deleteSubject = (id) => {
    if (window.confirm('Вы уверены, что хотите удалить этот предмет?')) {
      handleArrayChange('subjects', 'remove', { id });
    }
  };
  
  return (
    <div className="space-y-6">
      <h3 className="text-xl font-medium text-gray-900 dark:text-white flex items-center">
        <BookOpen className="w-5 h-5 mr-2 text-indigo-500" />
        Предметы
      </h3>
      
      {/* Форма добавления/редактирования предмета */}
      <div className="bg-gray-50 dark:bg-gray-700/30 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
        <h4 className="text-base font-medium text-gray-800 dark:text-gray-200 mb-3">
          {isEditing ? 'Редактирование предмета' : 'Добавление нового предмета'}
        </h4>
        
        <form onSubmit={handleSubmitSubject} className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Button
              type="submit"
              primary
              size="sm"
              icon={isEditing ? <Save className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            >
              {isEditing ? 'Сохранить' : 'Добавить предмет'}
            </Button>
            
            {isEditing && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                icon={<X className="w-4 h-4" />}
                onClick={resetForm}
              >
                Отменить
              </Button>
            )}
          </div>
        </form>
      </div>
      
      {/* Список предметов */}
      <div>
        <h4 className="text-base font-medium text-gray-800 dark:text-gray-200 mb-3">Список предметов</h4>
        
        {errors.subjects && (
          <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.subjects}
          </div>
        )}
        
        {formData.subjects.length === 0 ? (
          <div className="text-center p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-200 dark:border-gray-700">
            <p className="text-gray-500 dark:text-gray-400">Предметы не добавлены</p>
            <Button
              type="button"
              variant="link"
              size="sm"
              icon={<Plus className="w-4 h-4" />}
              onClick={() => document.getElementById('subjectName').focus()}
            >
              Добавить первый предмет
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {formData.subjects.map((subject, index) => (
              <div 
                key={subject.id} 
                className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-center">
                  <div 
                    className="w-4 h-4 rounded-full mr-2 flex-shrink-0" 
                    style={{ backgroundColor: subject.color }}
                  ></div>
                  <div className="flex-grow font-medium text-gray-800 dark:text-gray-200">
                    {subject.name}
                  </div>
                  <div className="flex space-x-1">
                    <button
                      type="button"
                      onClick={() => startEditingSubject(subject)}
                      className="text-gray-500 hover:text-indigo-600 dark:text-gray-400 dark:hover:text-indigo-400"
                      title="Редактировать"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteSubject(subject.id)}
                      className="text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                      title="Удалить"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {subject.hoursPerWeek} ч. в неделю
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Информация */}
      <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-100 dark:border-blue-800">
        <div className="flex items-start">
          <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-3 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">
              Рекомендации по учебной нагрузке
            </h4>
            <p className="text-xs text-blue-600 dark:text-blue-400">
              Согласно СанПиН и ФГОС, недельная нагрузка по основным предметам в старших классах составляет: 
              русский язык - 3-4 часа, математика - 4-6 часов, история - 2-3 часа, иностранный язык - 3 часа, 
              физика/химия/биология - 2-3 часа каждый. Для ВУЗов стандартная нагрузка по дисциплине составляет 
              2-4 академических часа в неделю.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubjectsSection;